import { memo, useMemo } from "react";
import { getCircleSize } from "../utils";
import { CircuitRoute } from "./CircuitRoute";
import { RouteNodeHandles } from "./RouteNodeHandles";
import { splitPathsBySelection } from "./splitPathsBySelection";
import { DestinationMarker } from "../DestinationMarker";
import { LabeledCircle } from "../LabeledCircle";
import { CableTakeoffMarkers } from "../CableTakeoffMarkers";
import {
  ROUTE_OPACITY_AT_LOW_ZOOM,
  ROUTE_OPACITY_AT_HIGH_ZOOM,
  ROUTE_FADED_OPACITY_AT_LOW_ZOOM,
  ROUTE_FADED_OPACITY_AT_HIGH_ZOOM,
  ROUTE_DIMMED_OPACITY,
  ROUTE_LIVE_OPACITY,
  MARKER_OPACITY_AT_LOW_ZOOM,
  MARKER_OPACITY_AT_HIGH_ZOOM,
  STRANDED_DASH,
  STRANDED_STROKE_WIDTH,
  FEEDER_STROKE_FACTOR,
} from "./constants";
import type { CircuitPathsLayerProps } from "./types";
import type { CircuitPathResult } from "../../../hooks/useCircuitCablePaths/types";

const isPathVisible = (path: CircuitPathResult): boolean =>
  path.reachable && path.waypoints.length > 0;

const rgbStr = (rgb?: [number, number, number]): string | undefined =>
  rgb ? `rgb(${rgb[0]},${rgb[1]},${rgb[2]})` : undefined;

export const CircuitPathsLayer = memo(function CircuitPathsLayer({
  circuitPaths,
  selectedObject,
  zoomLevel,
  circleSizeScale,
  opacityThreshold,
  isHoverEnabled = true,
  strokeScale = 1,
  boardMarkers,
  ghostMarker,
  removedMarkers,
  unroutedDeviceMarkers,
  highlightMarker,
  heightMarks,
  candidateDeviceMarkers,
  selectedDeviceMarkers,
  selectedDeviceMarkersBlink,
  zoneDraft,
  feedAreas,
  planWalls,
  wallDraft,
  wallSelection,
  nodeHandlesForRouteId,
  reshapeView,
  areRouteLinesHidden,
}: CircuitPathsLayerProps) {
  const size = getCircleSize(zoomLevel) * (circleSizeScale ?? 1);
  const isLowZoom = zoomLevel < opacityThreshold;

  const markerOpacity = isLowZoom
    ? MARKER_OPACITY_AT_LOW_ZOOM
    : MARKER_OPACITY_AT_HIGH_ZOOM;

  const routeOpacity = isLowZoom
    ? ROUTE_OPACITY_AT_LOW_ZOOM
    : ROUTE_OPACITY_AT_HIGH_ZOOM;

  const fadedRouteOpacity = isLowZoom
    ? ROUTE_FADED_OPACITY_AT_LOW_ZOOM
    : ROUTE_FADED_OPACITY_AT_HIGH_ZOOM;

  const { nonSelectedPaths, selectedPaths } = useMemo(
    () =>
      splitPathsBySelection({
        paths: circuitPaths.paths,
        selectedId: selectedObject?.id,
      }),
    [circuitPaths.paths, selectedObject?.id],
  );

  const hasSelection = !!selectedObject?.id;

  const isBeingReshaped = (path: CircuitPathResult): boolean =>
    !!nodeHandlesForRouteId && path.routeId === nodeHandlesForRouteId;

  // While one run is open for reshaping it is the only cable that matters, so
  // it goes to full strength and every other one steps back. `resting` is what
  // this cable would have been drawn at with no mode open, and is what it keeps
  // when there is none.
  const routeOpacityOf = (path: CircuitPathResult, resting: number): number => {
    if (isBeingReshaped(path)) return ROUTE_LIVE_OPACITY;
    if (nodeHandlesForRouteId) return ROUTE_DIMMED_OPACITY;
    return resting;
  };

  // Only the run's colour is taken from the drawn paths; the marks themselves
  // come from the edit layer, which owns the graph the click acts on.
  const reshapeRgb = useMemo(
    () =>
      nodeHandlesForRouteId
        ? circuitPaths.paths.find(
            (path) => path.routeId === nodeHandlesForRouteId,
          )?.rgb
        : undefined,
    [circuitPaths.paths, nodeHandlesForRouteId],
  );

  return (
    <>
      {/* Lengths he DREW that no cable runs over. They persist now, so they
          have to be on the drawing when the mode is shut — a stroke that is
          saved and invisible is the same complaint as one that is thrown away.
          Dashed says what it is: on the plan, and in no total.

          Skipped while the mode is open on this run, because then the reshape
          view IS the live truth for it and the stored copy is one edit behind. */}
      {!reshapeView &&
        circuitPaths.strandedEdges?.map((edge, index) => (
          <path
            key={`stranded:${index}`}
            data-testid="circuit-stranded-edge"
            d={`M${edge.a.x},${edge.a.y} L${edge.b.x},${edge.b.y}`}
            fill="none"
            stroke={`rgb(${(reshapeRgb ?? circuitPaths.paths[0]?.rgb ?? [255, 255, 0]).join(",")})`}
            strokeWidth={STRANDED_STROKE_WIDTH}
            strokeDasharray={STRANDED_DASH}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      {nonSelectedPaths.map((path) => {
        if (!isPathVisible(path)) return null;
        const lastWaypoint = path.waypoints[path.waypoints.length - 1];
        return (
          <g key={path.destinationId}>
            {/* The DEVICES always stay: what "too messy when all is drawn"
                means is eighty-three cables crossing each other, not the
                symbols they end at. Hiding those too left him a blank plan.
                The run being RESHAPED keeps its cable whatever the switch
                says: every mark of the mode hangs off that cable, and a shape
                he cannot see is a shape he cannot reshape. */}
            {(!areRouteLinesHidden || isBeingReshaped(path)) && (
              <CircuitRoute
                waypoints={path.waypoints}
                rgb={path.rgb}
                isLive={isBeingReshaped(path)}
                opacity={routeOpacityOf(
                  path,
                  hasSelection ? fadedRouteOpacity : routeOpacity,
                )}
                isHoverEnabled={isHoverEnabled}
                strokeScale={path.isFeeder ? strokeScale * FEEDER_STROKE_FACTOR : strokeScale}
              />
            )}
            {/* A feeder ends on a board, which carries its own marker; a device dot there would be a device. */}
            {(!hasSelection || areRouteLinesHidden) && !path.isFeeder && (
              <DestinationMarker
                x={lastWaypoint.x}
                y={lastWaypoint.y}
                isSelected={false}
                opacity={markerOpacity}
                isHoverEnabled={isHoverEnabled}
                fillColor={rgbStr(path.rgb)}
              />
            )}
            {path.markerLabel && !isLowZoom && (
              <text
                data-testid="circuit-marker-label"
                x={lastWaypoint.x + size * 0.45}
                y={lastWaypoint.y - size * 0.45}
                fontSize={size * 0.7}
                fontWeight={700}
                fontFamily="Arial"
                fill={rgbStr(path.rgb) ?? "#334155"}
                stroke="#ffffff"
                strokeWidth={3}
                paintOrder="stroke"
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              >
                {path.markerLabel}
              </text>
            )}
          </g>
        );
      })}
      {selectedPaths.map((path) => {
        if (!isPathVisible(path)) return null;
        const lastWaypoint = path.waypoints[path.waypoints.length - 1];
        return (
          <g key={path.destinationId}>
            {(!areRouteLinesHidden || isBeingReshaped(path)) && (
              <CircuitRoute
                waypoints={path.waypoints}
                rgb={path.rgb}
                isSelected
                isLive={isBeingReshaped(path)}
                opacity={routeOpacityOf(path, routeOpacity)}
                isHoverEnabled={isHoverEnabled}
                strokeScale={path.isFeeder ? strokeScale * FEEDER_STROKE_FACTOR : strokeScale}
              />
            )}
            {!path.isFeeder && (
              <DestinationMarker
                x={lastWaypoint.x}
                y={lastWaypoint.y}
                isSelected
                opacity={markerOpacity}
                isHoverEnabled={isHoverEnabled}
                fillColor={rgbStr(path.rgb)}
              />
            )}
          </g>
        );
      })}
      {circuitPaths.distributionBoardPosition && (
        <LabeledCircle
          x={circuitPaths.distributionBoardPosition.x}
          y={circuitPaths.distributionBoardPosition.y}
          radius={size / 2}
          fillColor="#eab308"
          strokeColor="#eab308"
          label="S"
          opacity={markerOpacity}
        />
      )}
      <CableTakeoffMarkers
        boardMarkers={boardMarkers}
        ghostMarker={ghostMarker}
        removedMarkers={removedMarkers}
        unroutedDeviceMarkers={unroutedDeviceMarkers}
        highlightMarker={highlightMarker}
        heightMarks={heightMarks}
        candidateDeviceMarkers={candidateDeviceMarkers}
        selectedDeviceMarkers={selectedDeviceMarkers}
        selectedDeviceMarkersBlink={selectedDeviceMarkersBlink}
        zoneDraft={zoneDraft}
        feedAreas={feedAreas}
        planWalls={planWalls}
        wallDraft={wallDraft}
        wallSelection={wallSelection}
        size={size}
        isLowZoom={isLowZoom}
      />
      {/* Last of everything, so no marker can cover a target or take the
          pointer off one. The marks ARE the offer, and the run they belong to
          is the only one left on the plan while the mode is open. */}
      {reshapeView && (
        <RouteNodeHandles
          view={reshapeView}
          rgb={reshapeRgb}
          size={size}
          zoomLevel={zoomLevel}
        />
      )}
    </>
  );
});
